import type { ProjektMerkmale } from '../planungsphase/types';

/**
 * Kapitelmodell des Moduls 06 „Baustellenordnung" (Spezifikation Abschnitt C).
 *
 * ═══ DER GRUNDSATZ ════════════════════════════════════════════════════════
 * Ein Kapitel ist DOKUMENTNEUTRAL: seine Kennung sagt, WOVON es handelt, nie,
 * WO es gedruckt wird. Es gibt deshalb genau EINEN Katalog `KAPITEL` und
 * daneben `DOKUMENTE` — je Dokument eine eigene, geordnete Kapitelliste.
 * Dasselbe Kapitel darf in mehreren Listen stehen.
 *
 * Zwei Kataloge `KAPITEL_ORDNUNG`/`KAPITEL_HSE` waeren der Fehler, den die
 * Vorfassung gemacht hat: sie fuehrte `deckblatt`/`h_deckblatt` als zwei
 * Kapitel, obwohl es eines ist. Ein gemeinsames Kapitel staende dann zweimal
 * in den Daten, und die beiden Staende liefen auseinander.
 *
 * ═══ WARUM DIE BEIDEN LISTEN SO UNGLEICH LANG SIND ════════════════════════
 * Die Baustellenordnung ist die Hausordnung: sieben Kapitel plus Rahmen. Ihr
 * Massstab ist der BG-BAU/INQA-Leitfaden, und dort ist Laenge ein MANGEL
 * („Baustellenordnungen sind zu umfangreich und werden nicht gelesen").
 * Der HSE-Plan ist das Gegenteil — ein Managementplan nach SCC-VAZ 2021
 * Dok. 003 Kapitel 5, in der Praxis mit Basic-Teil, taetigkeitsbezogenen
 * Anlagen und Anhaengen. Seine Gliederung folgt hier einem realen Plan
 * (Neubau eines Labor- und Buerogebaeudes, ARGE aus Hochbau und TGA, 64
 * Seiten): Teil 1 die uebergreifenden Regelungen, Teil 2 die Anlagen je
 * Gefaehrdung, danach die Anhaenge. Die Ordnung deshalb NICHT „mitwachsen
 * lassen" — sie waere damit kaputt, nicht vollstaendig.
 */

export type Dokumentart = 'ordnung' | 'hse';

/** Woher der Inhalt eines Kapitels kommt. Haengt am Gegenstand, nicht am Deckblatt. */
export type Datenquelle = 'abgeleitet' | 'erfasst' | 'katalog' | 'ki';

/**
 * Die Merkmale von `ProjektMerkmale`, die wirklich Ja/Nein tragen.
 *
 * Zahlenmerkmale wie `absturzhoeheM` (`planungsphase/types.ts`) sind hier
 * ausgeschlossen, und das ist der Zweck des Typs: eine Zahl kann nie
 * „ausdruecklich `false`" sein, die dreiwertige Logik greift daran nicht, und
 * in einer `wenn`-Liste wuerde sie stillschweigend als wahrheitswertig
 * behandelt (Spezifikation C.1).
 */
export type BooleanMerkmal = {
    [K in keyof ProjektMerkmale]-?: NonNullable<ProjektMerkmale[K]> extends boolean ? K : never;
}[keyof ProjektMerkmale];

/**
 * Dieselben Schluessel noch einmal zur Laufzeit.
 *
 * TypeScript-Typen sind im Test nicht da; `kapitel.test.ts` prueft die
 * `wenn`/`nichtWenn`-Listen gegen DIESE Liste. Der Typ oben haelt sie
 * vollstaendig: faellt ein Merkmal aus `ProjektMerkmale` weg oder wird aus
 * einem `boolean` eine Zahl, bricht die Zuweisung beim Uebersetzen.
 */
export const BOOLEAN_MERKMALE: readonly BooleanMerkmal[] = [
    'mehrereArbeitgeber', 'bestand', 'abbruch', 'erdarbeiten', 'strassenbaustelle',
    'betriebLaeuftWeiter', 'fertigteilmontage', 'kran', 'geruest', 'dacharbeiten',
    'freileitung', 'gewaesser', 'tunnelbau', 'taucharbeiten', 'druckluft',
    'sprengarbeiten', 'strahlung', 'gefahrstoffeAnhang2', 'tga', 'nachtarbeit',
    'winterbau', 'altlastenverdacht', 'unternehmerOhneBeschaeftigte', 'ortbeton',
];

/**
 * Praefix jeder selbst angelegten Kapitelkennung.
 *
 * Der Grund ist keine Kosmetik: der Katalog im Repo waechst weiter. Ohne
 * getrennte Namensraeume gaebe eine spaeter nachgezogene Kennung `brandschutz`
 * eine stille Kollision mit einem gleichnamigen eigenen Kapitel — und der
 * Inhalt eines Bestandsprojekts stuende ploetzlich unter einer fremden
 * Ueberschrift. Ein Test haelt beide Seiten der Regel fest.
 */
export const EIGEN_PRAEFIX = 'eigen_';

/**
 * Ein Kapitel. Kein Feld nennt eine Dokumentart, und keine Kennung traegt ein
 * Praefix `o`/`h` — beides wird von `kapitel.test.ts` festgehalten.
 */
export interface KapitelDefinition {
    /** z. B. 'notfallmanagement'. Eindeutig ueber den ganzen Katalog. */
    id: string;
    titel: string;
    quelle: Datenquelle | Datenquelle[];
    /**
     * Bedingung fuer das Ziehen, ODER-verknuepft: das Kapitel faellt erst weg,
     * wenn JEDES genannte Merkmal ausdruecklich `false` ist. Fehlt die Liste,
     * wird das Kapitel immer gezogen.
     */
    wenn?: BooleanMerkmal[];
    /**
     * Ausschluss. Das Kapitel entfaellt, sobald eines dieser Merkmale
     * ausdruecklich `true` ist.
     *
     * Die Spezifikation (C) schreibt an dieser Stelle „ausdruecklich `false`".
     * Das ist ein Verschreiber: so gelesen waere `nichtWenn: [x]` dasselbe wie
     * `wenn: [x]`. Massgeblich ist die dreiwertige Logik des Bestands, auf die
     * die Spezifikation selbst verweist — `planungLogic.ts` `frageTrifftZu`:
     * „Ausschluss wirkt nur bei ausdruecklichem `true`".
     */
    nichtWenn?: BooleanMerkmal[];
    /** Selbst angelegt statt aus dem Repo-Katalog. Wird nie von Hand gesetzt. */
    eigen?: boolean;
}

/**
 * EIN Katalog fuer alle Dokumente, nach `id` eindeutig.
 *
 * Die Reihenfolge hier ist Ordnung fuer das Auge, nicht Druckreihenfolge —
 * die steht an `DOKUMENTE`.
 */
export const KAPITEL: KapitelDefinition[] = [
    // ═══ Rahmen ══════════════════════════════════════════════════════════
    { id: 'deckblatt', titel: 'Deckblatt und Geltung', quelle: 'abgeleitet' },
    { id: 'revisionsverzeichnis', titel: 'Revisionsverzeichnis', quelle: 'abgeleitet' },
    { id: 'offene_punkte', titel: 'Offene Punkte vor Baubeginn', quelle: 'abgeleitet' },
    { id: 'kenntnisnahme', titel: 'Bestätigung der Kenntnisnahme', quelle: ['katalog', 'erfasst'] },
    { id: 'unterschrift', titel: 'Unterschrift der Verantwortlichen', quelle: 'erfasst' },

    // ═══ Grundlagen des Projekts ═════════════════════════════════════════
    { id: 'projektbeschreibung', titel: 'Projektbeschreibung und Bauvorhaben', quelle: 'abgeleitet' },
    { id: 'ecktermine', titel: 'Ecktermine und Bauphasen', quelle: ['abgeleitet', 'erfasst'] },
    { id: 'geltungsbereich', titel: 'Geltungsbereich und Adressaten', quelle: ['abgeleitet', 'erfasst'] },
    { id: 'allgemeine_informationen', titel: 'Allgemeine Informationen', quelle: ['abgeleitet', 'erfasst'] },
    {
        id: 'arbeitsgemeinschaft',
        titel: 'Besonderheit Arbeitsgemeinschaft',
        quelle: 'erfasst',
        // Die Rechtsform des Auftragnehmers steht am Kopfsatz (`org_modell`),
        // nicht in den Projektmerkmalen — deshalb keine Merkmalsbedingung,
        // sondern ein waehlbares Kapitel des Dokuments.
    },
    { id: 'mitgeltende_unterlagen', titel: 'Mitgeltende Unterlagen', quelle: ['abgeleitet', 'erfasst'] },
    { id: 'begriffe', titel: 'Begriffe und Abkürzungen', quelle: 'katalog' },
    { id: 'organisation', titel: 'HSE-Organisation und Verantwortlichkeiten', quelle: 'erfasst' },
    { id: 'mobilisierung', titel: 'Nachweise vor Arbeitsaufnahme (Mobilisierung)', quelle: ['katalog', 'erfasst'] },
    { id: 'dokumentenlenkung', titel: 'Dokumentenlenkung und Plattform', quelle: 'erfasst' },
    { id: 'aktenstruktur', titel: 'Aufbau der HSE-Akte', quelle: 'abgeleitet' },
    { id: 'phasenbezug', titel: 'Schwerpunkte je Bauphase', quelle: ['abgeleitet', 'erfasst'] },

    // ═══ Notfall ═════════════════════════════════════════════════════════
    { id: 'notfallmanagement', titel: 'Notfallorganisation und Meldewege', quelle: ['abgeleitet', 'erfasst'] },
    { id: 'sammelplaetze', titel: 'Sammelplätze, Flucht- und Rettungswege', quelle: ['abgeleitet', 'erfasst'] },
    { id: 'erste_hilfe', titel: 'Erste-Hilfe-Organisation', quelle: ['katalog', 'erfasst'] },

    // ═══ Grundsätze und Beurteilung ══════════════════════════════════════
    { id: 'grundsaetze', titel: 'Grundsätze des Arbeits-, Gesundheits- und Umweltschutzes', quelle: 'katalog' },
    { id: 'sicherheitskultur', titel: 'Förderung der Sicherheitskultur', quelle: ['katalog', 'erfasst'] },
    { id: 'gefaehrdungsbeurteilung', titel: 'Gefährdungsbeurteilung — Grundlage und Abgrenzung', quelle: 'abgeleitet' },
    { id: 'stop_work', titel: 'Arbeitsunterbrechung bei Gefahr (STOP WORK)', quelle: 'katalog' },
    { id: 'lmra', titel: 'Kurzbewertung vor Arbeitsaufnahme', quelle: 'katalog' },

    // ═══ Zusammenarbeit ══════════════════════════════════════════════════
    { id: 'zusammenarbeit', titel: 'Zusammenarbeit mit Auftraggeber und Dritten', quelle: 'katalog' },
    { id: 'praequalifikation', titel: 'Eignungsprüfung und Pflichten der Nachunternehmer', quelle: ['katalog', 'erfasst'] },
    { id: 'weisungsbefugnis', titel: 'Weisungsbefugnis und Zuständigkeiten', quelle: 'erfasst' },
    { id: 'nachunternehmer_konzept', titel: 'Sicherheitskonzept und Nachtrag des Nachunternehmers', quelle: 'katalog' },
    {
        id: 'koordination',
        titel: 'Benennung der koordinierenden Person (BaustellV)',
        quelle: 'abgeleitet',
        // Dasselbe Merkmal, das nach § 3 Abs. 1 BaustellV die
        // Koordinatorenpflicht traegt.
        wenn: ['mehrereArbeitgeber'],
    },
    { id: 'abstimmung', titel: 'Koordinierung der Arbeiten und Sitzungssystem', quelle: ['katalog', 'erfasst'] },

    // ═══ Zugang, Verkehr, Baustelle ══════════════════════════════════════
    { id: 'zugang', titel: 'Zugangsordnung und Baustellenausweis', quelle: 'katalog' },
    { id: 'arbeitszeiten', titel: 'Arbeitszeiten und Erreichbarkeit', quelle: 'erfasst' },
    { id: 'verkehrswege', titel: 'Verkehrswege', quelle: ['katalog', 'erfasst'] },
    { id: 'baustellenverkehr', titel: 'Baustellenverkehr, Zufahrten und Rettungswege', quelle: ['katalog', 'erfasst'] },
    { id: 'logistik', titel: 'Anlieferung, Logistik und Sondertransporte', quelle: 'erfasst' },
    { id: 'besucher', titel: 'Besucher und Dritte', quelle: 'katalog' },
    { id: 'medien', titel: 'Foto- und Filmaufnahmen', quelle: 'katalog' },
    { id: 'diebstahlschutz', titel: 'Sicherung gegen Diebstahl und Beschädigung', quelle: 'katalog' },
    { id: 'baustelleneinrichtung', titel: 'Baustelleneinrichtung und Flächen', quelle: ['abgeleitet', 'erfasst'] },
    { id: 'sozialeinrichtungen', titel: 'Sozialeinrichtungen', quelle: 'katalog' },
    { id: 'lagerung', titel: 'Materiallagerung und Verpackung', quelle: 'katalog' },
    { id: 'anschluesse', titel: 'Anschlüsse und Verteilungen', quelle: 'katalog' },
    { id: 'beleuchtung', titel: 'Beleuchtung', quelle: 'katalog' },
    { id: 'rauchen', titel: 'Rauchen', quelle: 'katalog' },
    { id: 'sauberkeit', titel: 'Ordnung, Sauberkeit und Hygiene', quelle: 'katalog' },
    { id: 'infektionsschutz', titel: 'Infektionsschutz', quelle: 'katalog' },
    { id: 'allgemeine_regelungen', titel: 'Allgemeine Regelungen', quelle: ['katalog', 'erfasst'] },
    // Kein Sanktionskapitel: an seine Stelle treten Hausrecht und Wegweisung
    // als tatsaechliche Massnahme und der Verweis auf den Bauvertrag
    // (Entscheidung des Auftraggebers, Spezifikation P). Kein Baustein
    // verhaengt Geldbetraege oder Vertragsstrafen; der eine Satz, der sie
    // nennt (`bo.regeln.folgen_bauvertrag`), schliesst sie ausdruecklich aus —
    // der Katalogtest haelt genau diese Ausnahme fest und faellt bei jeder
    // weiteren.
    { id: 'hausrecht', titel: 'Hausrecht, Wegweisung und Vertragsfolgen', quelle: ['katalog', 'erfasst'] },

    // ═══ Unterweisung und Personal ═══════════════════════════════════════
    { id: 'unterweisung', titel: 'Unterweisung eigener und Sub-Beschäftigter', quelle: 'katalog' },
    { id: 'qualifikationen', titel: 'Qualifikationsnachweise', quelle: 'katalog' },
    { id: 'arbeitnehmerueberlassung', titel: 'Arbeitnehmerüberlassung', quelle: 'katalog' },
    { id: 'sprache', titel: 'Sprache und Verständigung', quelle: 'katalog' },

    // ═══ Überwachung und Berichtswesen ═══════════════════════════════════
    { id: 'inspektionen', titel: 'Begehungen, Sicherheitsrundgänge und Audits', quelle: ['katalog', 'erfasst'] },
    { id: 'arbeitsfreigabe', titel: 'Arbeitserlaubnisverfahren', quelle: ['katalog', 'erfasst'] },
    { id: 'schutzeinrichtungen', titel: 'Erhalt und Wiederherstellung von Schutzeinrichtungen', quelle: 'katalog' },
    { id: 'ereignisse', titel: 'Ereignis-Berichterstattung (Unfälle, Beinaheunfälle)', quelle: 'katalog' },
    { id: 'kennzahlen', titel: 'Kennzahlen und monatliche Berichterstattung', quelle: ['katalog', 'erfasst'] },

    // ═══ Ausrüstung ══════════════════════════════════════════════════════
    { id: 'psa', titel: 'PSA-Mindestumfang und Grundausstattung', quelle: 'katalog' },
    { id: 'psaga', titel: 'PSA gegen Absturz', quelle: 'katalog' },
    {
        id: 'arbeitsmittel',
        titel: 'Arbeitsmittel mit unternehmensübergreifender Gefährdung',
        quelle: 'katalog',
        wenn: ['kran', 'geruest', 'erdarbeiten', 'fertigteilmontage'],
    },
    { id: 'arbeitsmittel_pruefung', titel: 'Arbeitsmittel, Prüfungen und Prüffristen', quelle: 'katalog' },
    { id: 'baumaschinen', titel: 'Baumaschinen, Werkzeuge und Ausrüstung', quelle: 'katalog' },

    // ═══ Brandschutz ═════════════════════════════════════════════════════
    { id: 'brandschutz', titel: 'Brandschutz, Alarmplan und Evakuierung', quelle: ['katalog', 'erfasst'] },

    // ═══ Umweltschutz ════════════════════════════════════════════════════
    { id: 'umwelt', titel: 'Umweltschutz — allgemeine Regelungen', quelle: 'katalog' },
    { id: 'nachhaltigkeit', titel: 'Anforderungen der Nachhaltigkeitszertifizierung', quelle: 'erfasst' },
    { id: 'abfall', titel: 'Abfall, Trennung und Entsorgung', quelle: 'katalog' },
    { id: 'laerm', titel: 'Lärm', quelle: 'katalog' },
    { id: 'staub', titel: 'Staub und Luftverunreinigungen', quelle: 'katalog' },
    { id: 'gewaesserschutz', titel: 'Gewässer-, Boden- und Grundwasserschutz', quelle: 'katalog' },
    { id: 'oeffentliche_sicherheit', titel: 'Öffentliche Sicherheit und Nachbarschaft', quelle: 'katalog' },

    // ═══ Anlagen: Tätigkeiten und Gefährdungen ═══════════════════════════
    {
        id: 'hoehenarbeiten',
        titel: 'Arbeiten in Höhen, Gerüste und Arbeitsbühnen',
        quelle: 'katalog',
        // `absturzhoeheM` steht hier bewusst NICHT: es ist eine Zahl und
        // gehoert nicht in eine dreiwertige Merkmalsliste (C.1).
        wenn: ['dacharbeiten', 'geruest', 'fertigteilmontage'],
    },
    {
        id: 'absturzsicherung',
        titel: 'Gemeinsam genutzte Schutzeinrichtungen gegen Absturz',
        quelle: 'katalog',
        wenn: ['dacharbeiten', 'geruest'],
    },
    {
        id: 'krane',
        titel: 'Krane, Hebezeuge und Anschlagen von Lasten',
        quelle: 'katalog',
        wenn: ['kran', 'fertigteilmontage'],
    },
    {
        id: 'erd_tiefbau',
        titel: 'Erd-, Tief- und Aushubarbeiten',
        quelle: 'katalog',
        wenn: ['erdarbeiten', 'abbruch', 'tunnelbau'],
    },
    { id: 'elektrische_anlagen', titel: 'Elektrische Anlagen und Betriebsmittel', quelle: 'katalog' },
    {
        id: 'freileitungen',
        titel: 'Arbeiten in der Nähe von Freileitungen',
        quelle: 'katalog',
        wenn: ['freileitung'],
    },
    { id: 'gefahrstoffe', titel: 'Gefahrstoffe, Gasflaschen und Lagerung', quelle: 'katalog' },
    { id: 'heissarbeiten', titel: 'Heißarbeiten', quelle: 'katalog' },
    { id: 'enge_raeume', titel: 'Arbeiten in engen Räumen', quelle: 'katalog' },
    { id: 'fahrzeuge', titel: 'Fahrzeuge und Flurförderzeuge', quelle: 'katalog' },
    { id: 'vibrationen', titel: 'Vibrationen', quelle: 'katalog' },
    { id: 'bodenoeffnungen', titel: 'Bodenöffnungen und Durchbrüche', quelle: 'katalog' },
    {
        id: 'strahlung',
        titel: 'Ionisierende Strahlung und Laserarbeiten',
        quelle: 'katalog',
        wenn: ['strahlung'],
    },
    {
        id: 'witterung',
        titel: 'Hitze, Kälte und Winterbau',
        quelle: 'katalog',
        wenn: ['winterbau', 'dacharbeiten', 'erdarbeiten'],
    },
    {
        id: 'gewaesserarbeiten',
        titel: 'Arbeiten an und über Wasser',
        quelle: 'katalog',
        wenn: ['gewaesser', 'taucharbeiten'],
    },
    { id: 'schwerpunkte', titel: 'Projektspezifische Gefährdungsschwerpunkte', quelle: ['erfasst', 'ki'] },

    // ═══ Anhänge ═════════════════════════════════════════════════════════
    { id: 'formularuebersicht', titel: 'Übersicht der Kapitel, Anlagen und Nachweise', quelle: 'abgeleitet' },
    { id: 'kennzahlenraster', titel: 'Berichtsraster der monatlichen Kennzahlen', quelle: 'katalog' },
    { id: 'rechtsgrundlagen', titel: 'Rechtsgrundlagen und Regelwerke', quelle: 'katalog' },
];

/** Wie die Kapitel eines Dokumentteils beziffert werden. */
export type Nummerierung = 'zahl' | 'buchstabe' | 'anhang' | 'ohne';

/**
 * Ein Abschnitt des Dokuments — beim HSE-Plan „Teil 1", „Teil 2 — Anlagen"
 * und die Anhänge. Die Baustellenordnung hat nur einen.
 */
export interface DokumentTeil {
    id: string;
    titel: string;
    /** Erklärender Vorspann des Teils, eine Zeile. */
    hinweis?: string;
    nummerierung: Nummerierung;
    /** Erste Ziffer bei `zahl`. Der Basic-Teil des HSE-Plans beginnt bei 0. */
    startNummer?: number;
}

/** Was EIN Dokument aus einem Kapitel macht. */
export interface DokumentKapitel {
    /** Muss in `KAPITEL` oder in den eigenen Kapiteln vorkommen (Test). */
    kapitel: string;
    /** Teil dieses Dokuments. Muss in `DokumentDefinition.teile` vorkommen (Test). */
    teil: string;
    pflicht: boolean;
    /** Waehlbares Zusatzkapitel dieses Dokuments — nur bei `pflicht: false`. */
    waehlbar?: boolean;
    /** Rahmenkapitel ohne Gliederungsziffer (Deckblatt, Unterschrift). */
    ohneNummer?: boolean;
}

/**
 * Ein Dokument mit seinen Teilen und der geordneten Kapitelliste.
 *
 * Die Reihenfolge im Array IST die Druckreihenfolge.
 */
export interface DokumentDefinition {
    art: Dokumentart;
    titel: string;
    untertitel: string;
    teile: DokumentTeil[];
    kapitel: DokumentKapitel[];
}

/**
 * Die Dokumente.
 *
 * `pflicht` und `waehlbar` haengen HIER und nicht am Kapitel: sie sind
 * Aussagen ueber die Rolle des Kapitels IN DIESEM Dokument.
 * `notfallmanagement` ist in der Baustellenordnung Pflicht und im HSE-Plan
 * waehlbar — staende `pflicht` am Kapitel, liesse sich das nicht
 * hinschreiben, ohne das Kapitel zu verdoppeln.
 */
export const DOKUMENTE: Record<Dokumentart, DokumentDefinition> = {
    ordnung: {
        art: 'ordnung',
        titel: 'Baustellenordnung',
        untertitel: 'Die Hausordnung der Baustelle — kurze Regelfassung',
        teile: [
            {
                id: 'haupt',
                titel: 'Baustellenordnung',
                hinweis: 'Bewusst kurz: eine Ordnung, die nicht gelesen wird, wirkt nicht.',
                nummerierung: 'zahl',
                startNummer: 1,
            },
        ],
        kapitel: [
            { kapitel: 'deckblatt', teil: 'haupt', pflicht: true, ohneNummer: true },
            { kapitel: 'allgemeine_informationen', teil: 'haupt', pflicht: true },
            { kapitel: 'notfallmanagement', teil: 'haupt', pflicht: true },
            { kapitel: 'allgemeine_regelungen', teil: 'haupt', pflicht: true },
            { kapitel: 'verkehrswege', teil: 'haupt', pflicht: true },
            { kapitel: 'anschluesse', teil: 'haupt', pflicht: true },
            { kapitel: 'arbeitsmittel', teil: 'haupt', pflicht: false },
            { kapitel: 'absturzsicherung', teil: 'haupt', pflicht: false },
            { kapitel: 'offene_punkte', teil: 'haupt', pflicht: true },
            { kapitel: 'kenntnisnahme', teil: 'haupt', pflicht: true, ohneNummer: true },
        ],
    },
    hse: {
        art: 'hse',
        titel: 'HSE-Plan',
        untertitel: 'Managementplan für Arbeits-, Gesundheits- und Umweltschutz',
        teile: [
            {
                id: 'rahmen',
                titel: 'Vorspann',
                nummerierung: 'ohne',
            },
            {
                id: 'basic',
                titel: 'Teil 1 — Übergreifende Regelungen',
                hinweis: 'Gilt für die gesamte Baustelle und alle dort tätigen Personen.',
                nummerierung: 'zahl',
                startNummer: 1,
            },
            {
                id: 'anlagen',
                titel: 'Teil 2 — Anlagen je Tätigkeit und Gefährdung',
                hinweis: 'Tätigkeits- und gefährdungsspezifische Anforderungen. Jede Anlage gilt zusätzlich zu Teil 1.',
                nummerierung: 'buchstabe',
            },
            {
                id: 'anhaenge',
                titel: 'Anhänge',
                nummerierung: 'anhang',
            },
        ],
        kapitel: [
            // ─── Vorspann ────────────────────────────────────────────────
            { kapitel: 'deckblatt', teil: 'rahmen', pflicht: true, ohneNummer: true },
            { kapitel: 'revisionsverzeichnis', teil: 'rahmen', pflicht: true, ohneNummer: true },

            // ─── Teil 1: Grundlagen ──────────────────────────────────────
            { kapitel: 'projektbeschreibung', teil: 'basic', pflicht: true },
            { kapitel: 'ecktermine', teil: 'basic', pflicht: true },
            { kapitel: 'geltungsbereich', teil: 'basic', pflicht: true },
            { kapitel: 'arbeitsgemeinschaft', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'mitgeltende_unterlagen', teil: 'basic', pflicht: true },
            { kapitel: 'begriffe', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'organisation', teil: 'basic', pflicht: true },
            { kapitel: 'weisungsbefugnis', teil: 'basic', pflicht: true },
            { kapitel: 'koordination', teil: 'basic', pflicht: false },
            { kapitel: 'mobilisierung', teil: 'basic', pflicht: true },
            { kapitel: 'dokumentenlenkung', teil: 'basic', pflicht: true },
            { kapitel: 'aktenstruktur', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'phasenbezug', teil: 'basic', pflicht: false, waehlbar: true },

            // ─── Teil 1: Notfall ─────────────────────────────────────────
            { kapitel: 'notfallmanagement', teil: 'basic', pflicht: true },
            { kapitel: 'sammelplaetze', teil: 'basic', pflicht: true },
            { kapitel: 'erste_hilfe', teil: 'basic', pflicht: true },
            { kapitel: 'brandschutz', teil: 'basic', pflicht: true },

            // ─── Teil 1: Grundsätze und Beurteilung ──────────────────────
            { kapitel: 'grundsaetze', teil: 'basic', pflicht: true },
            { kapitel: 'sicherheitskultur', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'gefaehrdungsbeurteilung', teil: 'basic', pflicht: true },
            { kapitel: 'stop_work', teil: 'basic', pflicht: true },
            { kapitel: 'lmra', teil: 'basic', pflicht: false, waehlbar: true },

            // ─── Teil 1: Zusammenarbeit ──────────────────────────────────
            { kapitel: 'zusammenarbeit', teil: 'basic', pflicht: true },
            { kapitel: 'praequalifikation', teil: 'basic', pflicht: true },
            { kapitel: 'nachunternehmer_konzept', teil: 'basic', pflicht: true },
            { kapitel: 'abstimmung', teil: 'basic', pflicht: true },

            // ─── Teil 1: Zugang, Verkehr, Baustelle ──────────────────────
            { kapitel: 'zugang', teil: 'basic', pflicht: true },
            { kapitel: 'arbeitszeiten', teil: 'basic', pflicht: true },
            { kapitel: 'verkehrswege', teil: 'basic', pflicht: true },
            { kapitel: 'baustellenverkehr', teil: 'basic', pflicht: true },
            { kapitel: 'logistik', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'besucher', teil: 'basic', pflicht: true },
            { kapitel: 'medien', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'diebstahlschutz', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'baustelleneinrichtung', teil: 'basic', pflicht: true },
            { kapitel: 'sozialeinrichtungen', teil: 'basic', pflicht: true },
            { kapitel: 'lagerung', teil: 'basic', pflicht: true },
            { kapitel: 'anschluesse', teil: 'basic', pflicht: true },
            { kapitel: 'beleuchtung', teil: 'basic', pflicht: true },
            { kapitel: 'rauchen', teil: 'basic', pflicht: true },
            { kapitel: 'sauberkeit', teil: 'basic', pflicht: true },
            { kapitel: 'infektionsschutz', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'hausrecht', teil: 'basic', pflicht: true },

            // ─── Teil 1: Unterweisung und Personal ───────────────────────
            { kapitel: 'unterweisung', teil: 'basic', pflicht: true },
            { kapitel: 'qualifikationen', teil: 'basic', pflicht: true },
            { kapitel: 'arbeitnehmerueberlassung', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'sprache', teil: 'basic', pflicht: true },

            // ─── Teil 1: Überwachung und Berichtswesen ───────────────────
            { kapitel: 'inspektionen', teil: 'basic', pflicht: true },
            { kapitel: 'arbeitsfreigabe', teil: 'basic', pflicht: true },
            { kapitel: 'schutzeinrichtungen', teil: 'basic', pflicht: true },
            { kapitel: 'ereignisse', teil: 'basic', pflicht: true },
            { kapitel: 'kennzahlen', teil: 'basic', pflicht: true },

            // ─── Teil 1: Ausrüstung ──────────────────────────────────────
            { kapitel: 'psa', teil: 'basic', pflicht: true },
            { kapitel: 'psaga', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'arbeitsmittel_pruefung', teil: 'basic', pflicht: true },
            { kapitel: 'baumaschinen', teil: 'basic', pflicht: true },

            // ─── Teil 1: Umweltschutz ────────────────────────────────────
            { kapitel: 'umwelt', teil: 'basic', pflicht: true },
            { kapitel: 'nachhaltigkeit', teil: 'basic', pflicht: false, waehlbar: true },
            { kapitel: 'abfall', teil: 'basic', pflicht: true },
            { kapitel: 'laerm', teil: 'basic', pflicht: true },
            { kapitel: 'staub', teil: 'basic', pflicht: true },
            { kapitel: 'gewaesserschutz', teil: 'basic', pflicht: true },
            { kapitel: 'oeffentliche_sicherheit', teil: 'basic', pflicht: true },

            // ─── Teil 2: Anlagen ─────────────────────────────────────────
            { kapitel: 'hoehenarbeiten', teil: 'anlagen', pflicht: false },
            { kapitel: 'absturzsicherung', teil: 'anlagen', pflicht: false },
            { kapitel: 'krane', teil: 'anlagen', pflicht: false },
            { kapitel: 'erd_tiefbau', teil: 'anlagen', pflicht: false },
            { kapitel: 'elektrische_anlagen', teil: 'anlagen', pflicht: true },
            { kapitel: 'freileitungen', teil: 'anlagen', pflicht: false },
            { kapitel: 'gefahrstoffe', teil: 'anlagen', pflicht: true },
            { kapitel: 'heissarbeiten', teil: 'anlagen', pflicht: true },
            { kapitel: 'enge_raeume', teil: 'anlagen', pflicht: true },
            { kapitel: 'fahrzeuge', teil: 'anlagen', pflicht: true },
            { kapitel: 'arbeitsmittel', teil: 'anlagen', pflicht: false },
            { kapitel: 'vibrationen', teil: 'anlagen', pflicht: false, waehlbar: true },
            { kapitel: 'bodenoeffnungen', teil: 'anlagen', pflicht: true },
            { kapitel: 'strahlung', teil: 'anlagen', pflicht: false },
            { kapitel: 'witterung', teil: 'anlagen', pflicht: false },
            { kapitel: 'gewaesserarbeiten', teil: 'anlagen', pflicht: false },
            { kapitel: 'schwerpunkte', teil: 'anlagen', pflicht: true },

            // ─── Anhänge ─────────────────────────────────────────────────
            { kapitel: 'formularuebersicht', teil: 'anhaenge', pflicht: false, waehlbar: true },
            { kapitel: 'offene_punkte', teil: 'anhaenge', pflicht: true },
            { kapitel: 'kennzahlenraster', teil: 'anhaenge', pflicht: false, waehlbar: true },
            { kapitel: 'rechtsgrundlagen', teil: 'anhaenge', pflicht: false, waehlbar: true },
            { kapitel: 'unterschrift', teil: 'anhaenge', pflicht: true, ohneNummer: true },
        ],
    },
};

// ═══════════════════════════════════════════════════════════════════════════
// EIGENE KAPITEL — projektübergreifend, aber nie im Repo
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein selbst angelegtes Kapitel.
 *
 * Es liegt NICHT im Repo, sondern firmenweit in der Datenbank, und es steht
 * damit in jedem neuen Dokument dieser Firma zur Verfügung — das ist der
 * Zweck. Zwei Dinge folgen daraus zwingend:
 *
 * 1. **Ein neues eigenes Kapitel erscheint in jedem offenen ENTWURF**, auch in
 *    dem eines fremden Projekts. Als Pflichtkapitel erscheint es dort leer und
 *    damit als offener Punkt. Das ist gewollt („leer ist nicht dasselbe wie
 *    fehlend"), aber niemand darf davon überrascht werden — deshalb legt die
 *    Erfassung ein neues Kapitel im Zweifel als **wählbar** an.
 * 2. **Freigegebene Fassungen bleiben unberührt.** Sie führen ihre gezogene
 *    Kapitelliste eingefroren mit sich (Spezifikation I.2); der Renderer liest
 *    dort den eingefrorenen Stand und nie diese Liste. Andernfalls wüchse ein
 *    unterschriebenes Dokument nachträglich um ein Kapitel.
 */
export interface EigenesKapitel {
    /** Kennung mit `EIGEN_PRAEFIX`, firmenweit eindeutig und unveränderlich. */
    id: string;
    titel: string;
    /** Für welches Dokument. Ein Kapitel für beide braucht zwei Einträge. */
    dokument: Dokumentart;
    /** Teil dieses Dokuments. */
    teil: string;
    /**
     * Kennung, hinter der es einsortiert wird. Fehlt sie oder ist sie
     * unbekannt, steht das Kapitel am Ende seines Teils — nie an einer
     * geratenen Stelle.
     */
    nach?: string;
    pflicht: boolean;
    waehlbar: boolean;
    quelle: Datenquelle | Datenquelle[];
    wenn?: BooleanMerkmal[];
    nichtWenn?: BooleanMerkmal[];
}

/** Ein Befund der Prüfung eines eigenen Kapitels. Leere Liste heißt: in Ordnung. */
export interface KapitelBefund {
    feld: 'id' | 'titel' | 'teil' | 'pflicht' | 'merkmale';
    text: string;
}

const slug = (text: string): string =>
    text.toLowerCase()
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40);

/**
 * Vorschlag für eine Kennung aus dem Titel.
 *
 * Sie wird EINMAL bei der Anlage vergeben und danach nie wieder berechnet:
 * an ihr hängen die erfassten Regelungen. Wer den Titel später ändert, ändert
 * die Kennung nicht — sonst verlöre das Kapitel seinen Inhalt.
 */
export const kapitelKennungAus = (titel: string, vergeben: Iterable<string> = []): string => {
    const stamm = `${EIGEN_PRAEFIX}${slug(titel) || 'kapitel'}`;
    const belegt = new Set(vergeben);
    if (!belegt.has(stamm)) return stamm;
    for (let i = 2; i < 1000; i += 1) {
        const kandidat = `${stamm}_${i}`;
        if (!belegt.has(kandidat)) return kandidat;
    }
    return `${stamm}_${Date.now()}`;
};

/** Prüft ein eigenes Kapitel gegen Katalog und Dokument. */
export const pruefeEigenesKapitel = (
    kapitel: EigenesKapitel,
    bestehende: EigenesKapitel[] = [],
): KapitelBefund[] => {
    const befunde: KapitelBefund[] = [];

    if (!kapitel.id.startsWith(EIGEN_PRAEFIX)) {
        befunde.push({ feld: 'id', text: `Die Kennung muss mit „${EIGEN_PRAEFIX}" beginnen.` });
    }
    if (KAPITEL.some(k => k.id === kapitel.id)) {
        befunde.push({ feld: 'id', text: 'Diese Kennung gehört bereits einem Kapitel des Katalogs.' });
    }
    if (bestehende.some(k => k !== kapitel && k.id === kapitel.id && k.dokument === kapitel.dokument)) {
        befunde.push({ feld: 'id', text: 'Diese Kennung ist in diesem Dokument bereits vergeben.' });
    }
    if (!kapitel.titel.trim()) {
        befunde.push({ feld: 'titel', text: 'Ohne Überschrift lässt sich das Kapitel nicht drucken.' });
    }
    if (!DOKUMENTE[kapitel.dokument]?.teile.some(t => t.id === kapitel.teil)) {
        befunde.push({ feld: 'teil', text: 'Der gewählte Teil gehört nicht zu diesem Dokument.' });
    }
    if (kapitel.waehlbar && kapitel.pflicht) {
        befunde.push({ feld: 'pflicht', text: 'Ein wählbares Kapitel kann nicht zugleich Pflicht sein.' });
    }
    for (const merkmal of [...(kapitel.wenn ?? []), ...(kapitel.nichtWenn ?? [])]) {
        if (!BOOLEAN_MERKMALE.includes(merkmal)) {
            befunde.push({ feld: 'merkmale', text: `„${merkmal}" ist kein Ja/Nein-Merkmal des Projekts.` });
        }
    }
    return befunde;
};

/** Die eigenen Kapitel eines Dokuments, geprüft und in stabiler Folge. */
const eigeneFuer = (dokument: Dokumentart, eigene: EigenesKapitel[]): EigenesKapitel[] =>
    eigene.filter(k => k.dokument === dokument && pruefeEigenesKapitel(k, eigene).length === 0);

/**
 * Der Katalog einschließlich der eigenen Kapitel eines Dokuments.
 *
 * Nachschlagen läuft immer hierüber, nie über `KAPITEL` allein — sonst fiele
 * ein eigenes Kapitel beim Ziehen wortlos heraus.
 */
export const katalogMit = (dokument: Dokumentart, eigene: EigenesKapitel[] = []): KapitelDefinition[] => [
    ...KAPITEL,
    ...eigeneFuer(dokument, eigene).map(k => ({
        id: k.id,
        titel: k.titel,
        quelle: k.quelle,
        wenn: k.wenn,
        nichtWenn: k.nichtWenn,
        eigen: true,
    })),
];

/**
 * Die Kapitelliste eines Dokuments mit den eigenen Kapiteln an ihrer Stelle.
 *
 * Einsortiert wird hinter `nach`; ohne oder bei unbekannter Angabe ans Ende
 * des jeweiligen Teils. Geraten wird nie.
 */
export const dokumentKapitel = (
    dokument: Dokumentart,
    eigene: EigenesKapitel[] = [],
): DokumentKapitel[] => {
    const liste = [...DOKUMENTE[dokument].kapitel];

    for (const k of eigeneFuer(dokument, eigene)) {
        const eintrag: DokumentKapitel = {
            kapitel: k.id,
            teil: k.teil,
            pflicht: k.pflicht,
            waehlbar: k.waehlbar || undefined,
        };
        const nach = k.nach
            ? liste.findIndex(e => e.kapitel === k.nach && e.teil === k.teil)
            : -1;
        if (nach >= 0) {
            liste.splice(nach + 1, 0, eintrag);
            continue;
        }
        const letzterDesTeils = liste.map(e => e.teil).lastIndexOf(k.teil);
        if (letzterDesTeils >= 0) liste.splice(letzterDesTeils + 1, 0, eintrag);
        else liste.push(eintrag);
    }
    return liste;
};

// ═══════════════════════════════════════════════════════════════════════════
// ZIEHEN
// ═══════════════════════════════════════════════════════════════════════════

/** Kapitel plus seine Rolle in DIESEM Dokument. */
export interface GezogenesKapitel {
    definition: KapitelDefinition;
    pflicht: boolean;
    waehlbar: boolean;
    teil: DokumentTeil;
    /** Gliederungsziffer, z. B. „7", „C" oder „Anhang 2". Leer bei Rahmenkapiteln. */
    nummer: string;
    eigen: boolean;
}

/**
 * Dreiwertig: `undefined` heisst „kann sein" und blendet nichts aus, nur ein
 * ausdrueckliches `false` tut das. Dieselbe Regel wie `merkmalMoeglich` in
 * `planungsphase/planungLogic.ts` — dort schon geprueft.
 */
const merkmalMoeglich = (wert: boolean | undefined): boolean => wert !== false;

/** Trifft die Bedingung des Kapitels auf diese Projektmerkmale zu? */
export const kapitelTrifftZu = (
    definition: KapitelDefinition,
    merkmale: ProjektMerkmale,
): boolean => {
    const m = (merkmale || {}) as Record<string, unknown>;

    // Ausschluss wirkt nur bei ausdruecklichem `true` (frageTrifftZu).
    if (definition.nichtWenn?.some(k => m[k] === true)) return false;

    // ODER: eines der Merkmale muss moeglich sein. Alle ausdruecklich `false`
    // laesst das Kapitel weg — ein fehlender Schluessel nicht.
    if (definition.wenn?.length) {
        return definition.wenn.some(k => merkmalMoeglich(m[k] as boolean | undefined));
    }
    return true;
};

/** Nachschlag im Katalog des Repos. Für eigene Kapitel `katalogMit` nutzen. */
export const kapitelDefinition = (id: string): KapitelDefinition | undefined =>
    KAPITEL.find(k => k.id === id);

const BUCHSTABEN = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** Die Ziffer des `n`-ten gezogenen Kapitels eines Teils (0-basiert). */
export const nummerImTeil = (teil: DokumentTeil, index: number): string => {
    switch (teil.nummerierung) {
        case 'zahl':
            return String((teil.startNummer ?? 1) + index);
        case 'buchstabe':
            // Nach Z weiter mit AA, AB … statt stillschweigend abzubrechen.
            return index < BUCHSTABEN.length
                ? BUCHSTABEN[index]
                : `${BUCHSTABEN[Math.floor(index / BUCHSTABEN.length) - 1]}${BUCHSTABEN[index % BUCHSTABEN.length]}`;
        case 'anhang':
            return `Anhang ${index + 1}`;
        default:
            return '';
    }
};

/**
 * Die Kapitel EINES Dokuments in Druckreihenfolge, mit Teil und Ziffer.
 *
 * Laeuft ueber die Kapitelliste des Dokuments — NICHT ueber `KAPITEL` mit
 * einem Filter auf die Dokumentart. Was in welcher Folge gedruckt wird, steht
 * am Dokument.
 *
 * Die Ziffern werden NACH dem Ziehen vergeben: ein weggefallenes Kapitel darf
 * keine Lücke in der Gliederung hinterlassen.
 *
 * Die Funktion entscheidet NICHT, ob ein Kapitel fertig ist — das tun
 * `abschnittBefunde()` und die Lückenliste.
 *
 * @param zusatz gewaehlte waehlbare Kapitel DIESES Dokuments
 * @param eigene firmenweit angelegte Kapitel
 */
export const gezogeneKapitel = (
    dokument: Dokumentart,
    merkmale: ProjektMerkmale,
    zusatz: string[] = [],
    eigene: EigenesKapitel[] = [],
): GezogenesKapitel[] => {
    const gewaehlt = new Set(zusatz);
    const katalog = katalogMit(dokument, eigene);
    const teile = new Map(DOKUMENTE[dokument].teile.map(t => [t.id, t]));
    const zaehler = new Map<string, number>();

    return dokumentKapitel(dokument, eigene).reduce<GezogenesKapitel[]>((acc, eintrag) => {
        const definition = katalog.find(k => k.id === eintrag.kapitel);
        const teil = teile.get(eintrag.teil);
        if (!definition || !teil) return acc;

        const waehlbar = eintrag.waehlbar === true;
        if (waehlbar && !gewaehlt.has(eintrag.kapitel)) return acc;
        if (!kapitelTrifftZu(definition, merkmale)) return acc;

        let nummer = '';
        if (!eintrag.ohneNummer && teil.nummerierung !== 'ohne') {
            const index = zaehler.get(teil.id) ?? 0;
            nummer = nummerImTeil(teil, index);
            zaehler.set(teil.id, index + 1);
        }

        acc.push({
            definition,
            pflicht: eintrag.pflicht,
            waehlbar,
            teil,
            nummer,
            eigen: definition.eigen === true,
        });
        return acc;
    }, []);
};

/** Die gezogenen Kapitel nach Teilen gebündelt — für Anzeige und Inhaltsverzeichnis. */
export const kapitelNachTeilen = (
    gezogen: GezogenesKapitel[],
): { teil: DokumentTeil; kapitel: GezogenesKapitel[] }[] =>
    gezogen.reduce<{ teil: DokumentTeil; kapitel: GezogenesKapitel[] }[]>((acc, g) => {
        const letzter = acc[acc.length - 1];
        if (letzter && letzter.teil.id === g.teil.id) letzter.kapitel.push(g);
        else acc.push({ teil: g.teil, kapitel: [g] });
        return acc;
    }, []);
